import { useEffect, useState } from 'react';
import { type ParticipantView, type RoomPreview } from '@voxa/shared';
import { api, ApiError } from '../lib/api.ts';
import { useI18n } from '../i18n/index.tsx';
import { localizedLanguageName } from '../i18n/languageName.ts';

/**
 * Step 3: the joiner sees the language pair before committing a seat, then
 * takes it. The invite arrives in the URL fragment and is removed from the
 * address bar as soon as it is read — section 9 forbids it lingering there or
 * in any log.
 */
export function Join({
  roomId,
  onJoined,
}: {
  roomId: string;
  onJoined: (participant: ParticipantView) => void;
}) {
  const { t, language: ui } = useI18n();
  const [invite, setInvite] = useState<string | null>(null);
  const [preview, setPreview] = useState<RoomPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fragment = new URLSearchParams(location.hash.replace(/^#/, ''));
    const token = fragment.get('invite') ?? '';
    setInvite(token);
    // Out of the address bar before anything else runs: a screenshot or a
    // shoulder is enough to leak it otherwise.
    history.replaceState(null, '', `${location.pathname}${location.search}`);

    if (token === '') {
      setError(t.join.noInvite);
      return;
    }
    api.previewRoom(roomId, token).then(setPreview, (err: unknown) => {
      setError(err instanceof ApiError ? err.message : t.join.unavailable);
    });
  }, [roomId, t]);

  async function join() {
    if (invite === null || invite === '') return;
    setBusy(true);
    setError(null);
    try {
      onJoined(await api.joinRoom(roomId, { inviteToken: invite }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.join.joinFailed);
    } finally {
      setBusy(false);
    }
  }

  if (error && !preview) {
    return (
      <div className="card">
        <h1>{t.join.failedTitle}</h1>
        <div className="notice bad">{error}</div>
        <div className="actions">
          <a href="/">
            <button type="button" className="primary">
              {t.join.createYourOwn}
            </button>
          </a>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="card">
        <h1>{t.join.checking}</h1>
      </div>
    );
  }

  const full = preview.seatsTaken >= 2;

  return (
    <div className="card">
      <h1>{t.join.title}</h1>
      <p className="lede">
        {t.join.youWillHear(localizedLanguageName(preview.myLanguage, ui))}{' '}
        {t.join.theyHear(localizedLanguageName(preview.peerLanguage, ui))}{' '}
        {preview.sourceSubtitles && t.home.sourceSubtitlesReady}
      </p>

      {error && <div className="notice bad">{error}</div>}
      {full && <div className="notice bad">{t.join.full}</div>}

      <div className="actions">
        <button
          type="button"
          className="primary"
          disabled={busy || full}
          onClick={() => void join()}
        >
          {busy ? t.join.connecting : t.join.connect}
        </button>
      </div>
    </div>
  );
}
